import {
  bytesToGB,
  formatStorageUsageWithReserved,
  formatStorageValue,
  formatTruncated,
  truncateToDecimals,
} from '@qse/utils'
import React from 'react'

export interface DualProgressBarProps {
  /** Used value (e.g., storage already used) */
  used: number
  /** Total available value */
  total: number
  /** Allocated/pending value (e.g., storage allocated for uploads) */
  allocated?: number
  /** Pre-calculated percentage for used */
  usedPercentage?: number
  /** Height of the progress bar */
  height?: string
  /** Background color for the allocated layer */
  allocatedColor?: string
  /** Background color for the used layer */
  usedColor?: string
  /** Background color for the track */
  trackColor?: string
  /** Additional CSS classes */
  className?: string
  /** Show value display below progress bar */
  showValueDisplay?: boolean
  /** Unit for the values (e.g., 'GB', 'calls') */
  unit?: string
  /** Custom formatter for the used value */
  formatUsedValue?: (value: number) => string
  /** Custom formatter for the total value */
  formatTotalValue?: (value: number) => string
  /** Pending bytes */
  pendingBytes?: number
  /** Render in over-quota (danger) style */
  isOverQuota?: boolean
}

/**
 * Dual Progress Bar Component
 *
 * Shows two layers of progress:
 * - Allocated layer (background): Shows pending/allocated resources
 * - Used layer (foreground): Shows actually used resources
 *
 * Useful for storage indicators where you want to show both
 * current usage and pending uploads.
 */
export const DualProgressBar: React.FC<DualProgressBarProps> = ({
  used,
  total,
  usedPercentage,
  height = 'h-2',
  allocatedColor = 'bg-blue-200 dark:bg-blue-300/30',
  usedColor = 'bg-blue-600 dark:bg-blue-400',
  trackColor = 'bg-gray-200 dark:bg-white/10',
  className = '',
  showValueDisplay = false,
  unit = '',
  formatUsedValue,
  formatTotalValue,
  pendingBytes,
  isOverQuota = false,
}) => {
  // Detect over-quota when quota is 0 and used > 0
  const isActuallyOverQuota = isOverQuota || (total === 0 && used > 0)

  // Calculate percentages
  const usedPercent =
    usedPercentage !== undefined
      ? usedPercentage
      : total > 0
        ? truncateToDecimals((used / total) * 100)
        : used > 0
          ? 100 // Show 100% when quota is 0 and used > 0
          : 0
  const allocatedPercent = truncateToDecimals(
    total > 0
      ? ((used + (pendingBytes || 0)) / total) * 100
      : used + (pendingBytes || 0) > 0
        ? 100 // Show 100% when quota is 0 and allocated > 0
        : 0,
  )

  // Format the display text with reserved bytes using dynamic unit formatting
  const formatDisplayText = () => {
    // For storage (when unit is empty or 'bytes'), always use dynamic formatter
    // This ensures both used and pending values are displayed in appropriate units (KB/MB/GB)
    if (!unit || unit === 'bytes') {
      return formatStorageUsageWithReserved(used, total, pendingBytes)
    }

    // If custom formatters are provided, use the old logic for backward compatibility
    // This handles API quota which uses different formatting
    if (formatUsedValue || formatTotalValue) {
      // For storage values (when values are in bytes), use dynamic formatting
      // Detect storage by checking if values are very large (likely bytes)
      const isStorage = used > 1024 * 1024 || total > 1024 * 1024

      if (isStorage) {
        // Use dynamic formatting for storage values
        const usedFormatted = formatStorageValue(used)
        const totalFormatted = formatStorageValue(total)

        if (pendingBytes && pendingBytes > 0) {
          const pendingFormatted = formatStorageValue(pendingBytes)

          return `${usedFormatted} (+${pendingFormatted} Reserved) / ${totalFormatted}`
        }

        return `${usedFormatted} / ${totalFormatted}`
      }

      // For non-storage units (like 'calls'), use the unit-based formatting
      const usedGB = bytesToGB(used)
      const totalGB = bytesToGB(total)

      const usedFormatted = formatUsedValue
        ? formatUsedValue(usedGB)
        : `${formatTruncated(usedGB)} ${unit}`.trim()
      const totalFormatted = formatTotalValue
        ? formatTotalValue(totalGB)
        : `${formatTruncated(totalGB)} ${unit}`.trim()

      if (pendingBytes && pendingBytes > 0) {
        const pendingFormatted = `${formatTruncated(bytesToGB(pendingBytes))} ${unit}`.trim()

        return `${usedFormatted} (+${pendingFormatted} Reserved) / ${totalFormatted}`
      }

      return `${usedFormatted} / ${totalFormatted}`
    }

    // Fallback: For storage display, values are in bytes - use new dynamic formatter
    // This handles storage usage with dynamic unit selection (KB/MB/GB)
    return formatStorageUsageWithReserved(used, total, pendingBytes)
  }

  return (
    <div className="w-full">
      <div
        className={`relative ${height} w-full ${trackColor} rounded-full overflow-hidden ${className}`}
      >
        {/* Allocated Layer (background) - pale version */}
        {allocatedPercent > 0 && (
          <div
            className={`absolute top-0 left-0 h-full ${
              isActuallyOverQuota ? 'bg-red-600 dark:bg-red-500' : allocatedColor
            } transition-all duration-300`}
            style={{
              width: `${Math.min(allocatedPercent, 100)}%`,
            }}
          />
        )}
        {/* Used Layer (foreground) - clear vibrant color */}
        {usedPercent > 0 && (
          <div
            className={`absolute top-0 left-0 h-full ${
              isActuallyOverQuota ? 'bg-red-600 dark:bg-red-500' : usedColor
            } transition-all duration-300`}
            style={{
              width: `${Math.min(usedPercent, 100)}%`,
            }}
          />
        )}
      </div>

      {/* Value Display */}
      {showValueDisplay && (
        <div className="flex justify-between items-center mt-1">
          <span className="text-xs text-gray-600 dark:text-white/60">{formatDisplayText()}</span>
        </div>
      )}
    </div>
  )
}

export default DualProgressBar
